import './Keyboard.css'
import React, {useContext, useEffect, useState} from "react";
import LetterKey from "./keys/LetterKey.tsx";
import RemoveKey from "./keys/RemoveKey.tsx";
import SubmitKey from "./keys/SubmitKey.tsx";
import {dictionary} from "../../../../../../assets/dictionary/en.json";
import {ToastContext} from "../../../../../../components/CustomToast/ToastProvider.tsx";
import {useMusicContext} from "../../../../../../providers/musicProvider/music-player-context.tsx";
import {isEmptyOrNullOrUndefined} from "../../../../../../common/utils/Datautil.ts";

type KeyboardProps = {
    addWordToInputList: (word: string) => void;
    wrongLetters: string[];
    gameState: number;
    handleGameState: (state: number) => void;
    inputWords: string[],
    correctWord: string,
    hintLetter: { letter: string, state: number }
    setHintLetter: (hintLetter: { letter: string, state: number }) => void,
    hasHint: boolean,
    setHasHint: (hasHint: boolean) => void,
}

// 游戏键盘
const Keyboard: React.FC<KeyboardProps> = ({
                                               correctWord,
                                               addWordToInputList,
                                               wrongLetters,
                                               gameState,
                                               handleGameState,
                                               hintLetter,
                                               hasHint,
                                               setHasHint
                                           }) => {
    const {addToast} = useContext(ToastContext);

    // 全局音乐provider
    const musicManager = useMusicContext();
    const [inputWord, setInputWord] = useState<string>('');
    const [inputWordList, setInputWordList] = useState<string[]>(['', '', '', '', '']);
    // 是否可以继续添加单词
    const [canAdd, setCanAdd] = useState<boolean>(true);

    // 处理电脑键盘输入事件
    const handleKeyDown = (event: { key: string; preventDefault: () => void; }) => {
        const validKeys = /^[a-zA-Z\s\b]$/;
        // 删除
        if (event.key == "Backspace") {
            handleClickRemoveKey();
            return;
        }
        // enter
        if (event.key == "Enter") {
            handleClickSubmitKey();
            return;
        }
        // 处理其余26个字母
        if (!validKeys.test(event.key)) {
            event.preventDefault(); // 阻止非有效键的默认行为
        } else {
            const letter = event.key.toLowerCase();
            handleClickLetter(letter.slice(0, 1));
            // console.log(event.key)
        }
    };

    // 监测用户电脑键盘输入
    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            // 清理事件监听
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    useEffect(() => {
        // GA埋点

        console.log(hintLetter.letter)
        if (!isEmptyOrNullOrUndefined(hintLetter.letter)) {
            setHasHint(true)
        }
    }, []);

    // 监测hint和输入，如果hint有，输入无，就显示
    useEffect(() => {
        if (hintLetter.letter !== '' && (inputWord === '' || inputWord.length == 0)) {
            const list = ['', '', '', '', '']
            list[hintLetter.state] = hintLetter.letter;
            setInputWordList(list)
        }
    }, [hintLetter, inputWord]);

    // 点击提交按键
    const handleClickSubmitKey = () => {
        if (!canAdd) {
            return;
        }
        try {
            setCanAdd(false)
            musicManager.playEnterSound()
            if (gameState === 2 || gameState == 3) {
                addToast({type: 'warning', message: 'Game is over'});
                return
            }
            const isValidated = checkWordInDictionary(inputWord);
            if (!isValidated) {
                musicManager.playLetterErrorSound()
                addToast({type: 'error', message: 'Not in word list.'});
                return;
            }
            addWordToInputList(inputWord);
            clearInput();
        } finally {
            // 等待3秒
            setTimeout(() => {
                setCanAdd(true)
            }, 3000);
        }
    }


    // 点击删除按键
    const handleClickRemoveKey = () => {
        musicManager.playKeyboardSound()
        // 删除字母
        if (inputWord.length > 0) {
            const words = inputWord.slice(0, -1);
            setInputWord(words);
            splitInputWordToList(words);
        }
    }

    // 点击字母
    const handleClickLetter = (letter: string) => {
        // 如果游戏状态处于未开始，那么就点击字母开始
        if (gameState == 0) {
            // 将游戏设置未开始
            handleGameState(1);
        }
        // 限制最多只能输入5个字母
        if (inputWord.length >= 5) return;
        // word加上输入的字母
        const lowerWord = (inputWord + letter).toLowerCase();

        setInputWord(lowerWord);
        splitInputWordToList(lowerWord);
        musicManager.playKeyboardSound()
    }

    // 将inputWord中的字母拆分成数组，用于渲染键盘,方法
    const splitInputWordToList = (word: string) => {
        if (!word) {
            setInputWordList(['', '', '', '', ''])
            return
        }
        const lettersArray = word.toUpperCase();
        setInputWordList(lettersArray.split(""));
    }

    // 清空inputWord和inputWordList
    const clearInput = () => {
        setInputWord('');
        setInputWordList(['', '', '', '', '']);
    }

    // 根据传入的单词，比对单词是否在字典中，字典放在assets/dictionary中,同时加一个比对是否是正确单词
    const checkWordInDictionary = (word: string) => {
        return word.toLowerCase() in dictionary || correctWord.toLowerCase() === word.toLowerCase();
    }

    // 键盘字母
    // 第一行
    const firstRow = ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'];
    // 第二行
    const secondRow = ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'];
    // 第三行
    const thirdRow = ['Z', 'X', 'C', 'V', 'B', 'N', 'M'];

    return (
        <div className="keyboard-container">
            {/*顶部输入框*/}
            <div className="keyboard-word-input-container">
                {inputWordList.map((letter, index) => {
                    return <div key={index}
                                className={`letter-container my-roboto-medium ${(hasHint && inputWord) === '' ? 'keyboard-hint-btn-color' : 'letter-normal-color'}`}>{letter}</div>
                })}
            </div>
            {/*下方键盘区域*/}
            {/*第一行键盘*/}
            <div className="keyboard-key-row-container">
                {
                    firstRow.map((key) => {
                        return <LetterKey wrongLetters={wrongLetters} key={key} rowNum={1} label={key}
                                          onPress={(key) => handleClickLetter(key)}/>
                        // return <div key={key} onClick={()=>handleClickLetter(key)}
                        // className="keyboard-key keyboard-first-letter-container my-roboto-medium">{key}</div>
                    })
                }
            </div>
            {/*第二行键盘*/}
            <div className="keyboard-key-row-container right-slice-one">
                {
                    secondRow.map((key) => {
                        return <LetterKey wrongLetters={wrongLetters} key={key} rowNum={2} label={key}
                                          onPress={(key) => handleClickLetter(key)}/>
                        // return <div key={key} onClick={() => handleClickLetter(key)}
                        //             className="keyboard-key keyboard-second-letter-container my-roboto-medium">{key}</div>
                    })
                }
            </div>
            {/*第三行键盘*/}
            <div className="keyboard-key-row-container">
                {
                    thirdRow.map((key) => {
                        return <LetterKey wrongLetters={wrongLetters} key={key} rowNum={3} label={key}
                                          onPress={(key) => handleClickLetter(key)}/>

                        // return <div key={key} onClick={() => handleClickLetter(key)}
                        //             className="keyboard-key keyboard-third-letter-container my-roboto-medium">{key}</div>
                    })
                }
                <RemoveKey key="removeBtn" onPress={handleClickRemoveKey}/>
            </div>
            <div className="keyboard-key-row-container">
                {/*提交按钮*/}
                <SubmitKey key="submitKey" label="SUBMIT" onPress={handleClickSubmitKey}/>
            </div>
        </div>
    );
}
export default Keyboard;